import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import StaffingDestinationSchema from "work/staffing/_schemas/staffing-destination/staffing-destination-schema";
import { z } from "zod";

export type StaffingDestinationPageJSON = z.infer<
  typeof StaffingDestinationPageSchema.StaffingDestinationPageJSONZ
>;
export type StaffingDestinationPageParams = z.infer<
  typeof StaffingDestinationPageSchema.StaffingDestinationPageZ
>;

/**
 * One page of the work a viewer may staff, and how much of it there is.
 *
 * The count is part of the answer rather than something the picker infers from the page, because
 * a page is never the whole set and a picker that could not say so would be quietly truncating.
 * `totalIsExact` is the server admitting when it can only give a floor — see
 * `StaffingDestinationPageResponse` for which half of the eligibility rule the store cannot
 * evaluate.
 */
export default class StaffingDestinationPageSchema {
  public static readonly StaffingDestinationPageJSONZ = z.object({
    destinations: z.array(StaffingDestinationSchema.StaffingDestinationJSONZ),
    pageIndex: z.number().int().nonnegative(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalIsExact: z.boolean(),
  });

  public static readonly StaffingDestinationPageZ = z.object({
    destinations: z.array(StaffingDestinationSchema.StaffingDestinationZ),
    pageIndex: z.number().int().nonnegative(),
    pageSize: z.number().int().positive(),
    totalCount: z.number().int().nonnegative(),
    totalIsExact: z.boolean(),
  });

  public static validateJSON(json: unknown): StaffingDestinationPageJSON {
    return parseOrThrow(StaffingDestinationPageSchema.StaffingDestinationPageJSONZ, json, {
      logError: true,
      schemaName: "StaffingDestinationPageSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is StaffingDestinationPageJSON {
    try {
      StaffingDestinationPageSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: StaffingDestinationPageJSON): StaffingDestinationPageParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = StaffingDestinationPageSchema.validateJSON(json);
    return {
      destinations: parsed.destinations.map((destination) =>
        StaffingDestinationSchema.deserialize(destination),
      ),
      pageIndex: parsed.pageIndex,
      pageSize: parsed.pageSize,
      totalCount: parsed.totalCount,
      totalIsExact: parsed.totalIsExact,
    };
  }
}
